import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const profileSchema = z.object({
  profileId: z.number(),
  countryCode: z.string(),
  currencyCode: z.string(),
  timezone: z.string(),
  accountInfo: z.object({
    marketplaceStringId: z.string(),
    id: z.string(),
    type: z.string(),
    name: z.string(),
    validPaymentMethod: z.boolean(),
  }),
});

const inaccessibleProfilesResponseSchema = z.object({
  profiles_missing_write_access: z.array(profileSchema),
  profiles_with_invalid_payment_methods: z.array(profileSchema),
});

/**
 * A profile as the Amazon Ads API returns it, narrowed to what the app reads. Amazon omits
 * `validPaymentMethod` for some account types (agencies) and `subType` for most profiles.
 */
const remoteAmazonProfileSchema = z.object({
  profileId: z.number(),
  countryCode: z.string(),
  accountInfo: z.object({
    id: z.string(),
    type: z.string(),
    name: z.string(),
    subType: z.string().optional(),
    validPaymentMethod: z.boolean().optional(),
  }),
});

export type RemoteAmazonProfile = z.infer<typeof remoteAmazonProfileSchema>;

/** One connected Amazon Ads login (`AmazonAdsClient`) with the profiles it can reach that no other account owns. */
const amazonAdsClientSchema = z.object({
  client_id: z.number(),
  created_on: z.string().nullable(),
  amazon_user_id: z.string().nullable(),
  amazon_user_name: z.string().nullable(),
  amazon_user_email: z.string().nullable(),
  profiles: z.array(remoteAmazonProfileSchema),
});

export type AmazonAdsClient = z.infer<typeof amazonAdsClientSchema>;

/** The backend matches a seller by name, and a vendor by profile id (vendors can share a name). */
const profileSelectionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("seller"), name: z.string() }),
  z.object({ type: z.literal("vendor"), name: z.string(), profile_id: z.number() }),
]);

export type AmazonProfileSelection = z.infer<typeof profileSelectionSchema>;

export const profilesApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getMissingAdvertisingProfiles",
    method: "get",
    path: "/debug_missing_ads_profiles",
    status: 200,
    response: inaccessibleProfilesResponseSchema,
  })
    .addEndpoint({
      // The organizations view answers with the Amazon profiles of the current org's Ads credentials.
      alias: "getRemoteAmazonProfiles",
      method: "get",
      path: "/organizations?remote_profiles=true",
      parameters: [{ name: "filter_out_duplicates", type: "Query", schema: z.boolean().optional() }],
      status: 200,
      response: z.array(remoteAmazonProfileSchema),
    })
    .addEndpoint({
      alias: "getAmazonAdsClients",
      method: "get",
      path: "/advertising/allprofiles/",
      parameters: [{ name: "org_id", type: "Query", schema: z.string() }],
      status: 200,
      response: z.array(amazonAdsClientSchema),
    })
    .addEndpoint({
      alias: "importAmazonProfiles",
      method: "post",
      path: "/manage/refresh_profiles/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({ org: z.string(), selected_profiles: z.array(profileSelectionSchema) }),
        },
      ],
      status: 200,
      response: z.unknown(),
    })
    .build(),
);
